import { FloatingActionBar } from '@dev-spendesk/grapes';
import { add, format, fromNumber } from 'ezmoney';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import {
  NotificationType,
  useNotifications,
} from 'modules/app/notifications/hooks/useNotifications';
import type { AppDispatch } from 'modules/app/redux/store';
import { useRemoveRequestsFromRequestsDraftCache } from 'modules/requests/api/useRequestsDraftQuery';
import { fetchRequestsStatsSuccess } from 'modules/requests/redux/legacy/actions';
import { getRequestsStats } from 'modules/requests/redux/legacy/selectors';
import { type GetV2ApiCompanyIdRequestsDraftResponseOK } from 'src/client/spendesk-api-types';
import { companyAPI } from 'src/core/api/axios';
import { CancelRequestsModal } from 'src/core/modules/requests/list/components/RequestsListBox/components/CancelRequestsModal';
import { logger } from 'src/utils/datadog-log-wrapper';

type Props = {
  selectedRowIds: string[];
  setSelectedRowIds: (ids: string[]) => void;
  unselectRequests: () => void;
  requests: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'];
  setSubmitModalState: (
    state:
      | {
          totalAmount: string;
          numberOfRequests: number;
          submittedIds: string[];
        }
      | undefined,
  ) => void;
};

export const DraftRequestsTableActions = ({
  selectedRowIds,
  setSubmitModalState,
  unselectRequests,
  requests,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const company = useCompany();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const stats = useSelector(getRequestsStats);
  const removeRequestsFromRequestsDraftCache =
    useRemoveRequestsFromRequestsDraftCache();

  const deleteDraftRequest = async (
    request: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'][0],
  ) => {
    try {
      await companyAPI.delete(`/drafts/${request.id}`, {
        companyId: company.id,
      });
      removeRequestsFromRequestsDraftCache([request.id]);
      dispatch(
        fetchRequestsStatsSuccess({
          ...stats,
          drafts: stats.drafts - 1,
        }),
      );
    } catch {
      logger.warn(`[BULK] failed to delete draft request ${request.id}`, {
        scope: 'requests::actions::bulk',
        team: 'travel-and-expenses',
      });
    }
  };

  const bulkAction =
    (
      function_: (
        request: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'][0],
      ) => Promise<void>,
    ) =>
    async (
      requestsToBulk: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'],
    ) => {
      const results = await Promise.allSettled(
        requestsToBulk.map((request) => function_(request)),
      );
      const fulfilledAction = results.filter(
        (result) => result.status === 'fulfilled',
      ).length;

      if (fulfilledAction > 0) {
        unselectRequests();
      }

      pushNotif({
        type: fulfilledAction
          ? NotificationType.Success
          : NotificationType.Danger,
        message: t('requests.actions.successfulWithCount', {
          count: fulfilledAction,
        }),
      });
    };

  if (selectedRowIds.length === 0) {
    return null;
  }
  const selectedRequests = requests.filter((request) =>
    selectedRowIds.includes(request.id),
  );
  const submittableRequests = selectedRequests.filter(
    (request) => request.draftCompletion?.status === 'complete',
  );

  return (
    <>
      <FloatingActionBar
        actions={[
          {
            text: t('requests.deleteWithCount', {
              count: selectedRequests.length,
            }),
            iconName: 'trash',
            onClick: async () => {
              setIsDeleteModalOpen(true);
            },
          },
          ...(submittableRequests.length > 0
            ? [
                {
                  text: t('requests.submitWithCount', {
                    count: submittableRequests.length,
                  }),
                  iconName: 'circle-check' as const,
                  onClick: async () => {
                    const sum = submittableRequests.reduce(
                      (accumulator, row) => {
                        if (row.amounts.declared?.inCompanyCurrency) {
                          return add(
                            accumulator,
                            row.amounts.declared?.inCompanyCurrency,
                          );
                        }

                        return accumulator;
                      },
                      fromNumber(0, company.currency as string),
                    );
                    setSubmitModalState({
                      totalAmount: format(sum),
                      numberOfRequests: submittableRequests.length,
                      submittedIds: submittableRequests.map(
                        (request) => request.id,
                      ),
                    });
                  },
                },
              ]
            : []),
        ]}
      >
        {t('misc.nSelected', { count: selectedRowIds.length })}
      </FloatingActionBar>
      <CancelRequestsModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        onConfirm={async () => {
          await bulkAction(deleteDraftRequest)(selectedRequests);
          setIsDeleteModalOpen(false);
        }}
      />
    </>
  );
};
